import { create } from 'zustand';
import { supabase, startSessionMonitoring, stopSessionMonitoring, forceReconnect } from '../lib/supabase';

interface Profile {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
}

interface Subscription {
  id: string;
  user_id: string;
  plan: string;
  status: string;
  next_billing_date: string;
  created_at: string;
  updated_at: string;
  last_renewal_date: string;
  request_count: number;
}

interface AuthState {
  user: any | null;
  profile: Profile | null;
  subscription: Subscription | null;
  promptCount: number;
  loading: boolean;
  error: string | null;
  setUser: (user: any | null) => void;
  setProfile: (profile: Profile | null) => void;
  setSubscription: (subscription: Subscription | null) => void;
  setPromptCount: (count: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  refreshSubscription: () => Promise<void>;
  initialize: () => Promise<boolean>;
  logout: () => Promise<void>;
  checkAndRefreshSession: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  subscription: null,
  promptCount: 0,
  loading: false,
  error: null,

  // Setters
  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile }),
  setSubscription: (subscription) => set({ subscription }),
  setPromptCount: (promptCount) => set({ promptCount }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  // Verifica e atualiza a sessão
  checkAndRefreshSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        await supabase.auth.signOut();
        return false;
      }
      
      // Verifica se o token está prestes a expirar (5 minutos de margem)
      const expiresAt = new Date(session.expires_at * 1000);
      const now = new Date();
      const shouldRefresh = expiresAt.getTime() - now.getTime() < 5 * 60 * 1000;
      
      if (shouldRefresh) {
        const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          await supabase.auth.signOut();
          return false;
        }
        set({ user: refreshedSession?.user ?? null });
        return !!refreshedSession?.user;
      }
      
      return !!session.user;
    } catch (error) {
      console.error('Failed to check session:', error);
      return false;
    }
  },

  // Atualiza os dados da assinatura
  refreshSubscription: async () => {
    const { user } = get();
    if (!user) return;

    try {
      set({ loading: true, error: null });
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      set({ subscription: data });
    } catch (error) {
      console.error('Failed to refresh subscription:', error);
      set({ error: 'Failed to load subscription data' });
    } finally {
      set({ loading: false });
    }
  },

  // Inicializa o estado de autenticação
  initialize: async () => {
    try {
      set({ loading: true, error: null });
      
      startSessionMonitoring();
      
      const sessionValid = await get().checkAndRefreshSession();
      if (!sessionValid) {
        set({ 
          user: null,
          profile: null,
          subscription: null,
          promptCount: 0,
          error: null
        });
        return false;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      set({ user: session.user });

      // Busca dados em paralelo
      const [profileRes, subscriptionRes, promptCountRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', session.user.id).single(),
        supabase.from('subscriptions').select('*').eq('user_id', session.user.id).single(),
        supabase.from('prompt_history')
          .select('*', { count: 'exact' })
          .eq('user_id', session.user.id)
          .gte('created_at', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString())
      ]);

      set({
        profile: profileRes.error ? null : profileRes.data,
        subscription: subscriptionRes.error ? null : subscriptionRes.data,
        promptCount: promptCountRes.count || 0
      });

      return !!session.user;
    } catch (error) {
      console.error('Auth initialization failed:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Authentication error',
        user: null,
        profile: null,
        subscription: null,
        promptCount: 0
      });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Realiza logout
  logout: async () => {
    try {
      set({ loading: true });
      await supabase.auth.signOut();
      stopSessionMonitoring();
      set({ 
        user: null,
        profile: null,
        subscription: null,
        promptCount: 0,
        error: null
      });
    } catch (error) {
      console.error('Logout failed:', error);
      set({ error: 'Failed to logout' });
    } finally {
      set({ loading: false });
    }
  }
}));

// Listener para mudanças de autenticação
supabase.auth.onAuthStateChange(async (event, session) => {
  const store = useAuthStore.getState();
  
  if (event === 'SIGNED_IN' && session) {
    await store.initialize();
  } else if (event === 'SIGNED_OUT') {
    store.setUser(null);
    store.setProfile(null);
    store.setSubscription(null);
    store.setPromptCount(0);
  } else if (event === 'TOKEN_REFRESHED') {
    store.setUser(session?.user ?? null);
  }
});

// Atualiza quando a página ganha foco
if (typeof window !== 'undefined') {
  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
      await useAuthStore.getState().checkAndRefreshSession();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
}

export default useAuthStore;